import React from "react";
import { Box, Chip, Stack, Theme, useTheme } from "@mui/material";
import { Link } from "react-router-dom";
import {
  Stock,
  StockCheckRequestStatus,
  getStockCheckRequestStatusLabel,
} from "../../../../../../models/Stock/Stock";
import { formatDate } from "../../../../../../views/components/common/functions/formatDate";
import CustomTypography from "../../../../../../views/components/common/form/CustomTypography";
import AppButton from "../../../../../../views/components/common/buttons/AppButton";
import { getModernPageColors } from "../../../../../../styles/modernListingPageSx";

type StockCheckRequestCardProps = {
  stock: Stock;
};

const getCardDivider = (theme: Theme) =>
  `1px solid ${theme.palette.customColors.lightGray}`;

const getCardBackgroundColor = (stock: Stock, theme: Theme) => {
  const { blue, green, red, orange } = theme.palette;
  const label = (stock.status_label || "").trim().toUpperCase();
  const status = stock.status;

  if (status === StockCheckRequestStatus.OPEN || label === "OPEN") {
    return `${blue.main}10`;
  }
  if (status === StockCheckRequestStatus.CLOSED || label === "CLOSED") {
    return `${green.main}10`;
  }
  if (status === StockCheckRequestStatus.REJECTED || label === "REJECTED") {
    return `${red.main}10`;
  }
  if (status === StockCheckRequestStatus.EXPIRED || label === "EXPIRED") {
    return `${orange.main}10`;
  }
  return theme.palette.customColors.white;
};

const titleCase = (value?: string | null) => {
  if (!value?.trim()) return "";
  return value
    .toLowerCase()
    .replace(/\b\w/g, (char) => char.toUpperCase());
};

const formatTat = (value?: string | number | null) => {
  const minutes = Number(value);
  if (!value || Number.isNaN(minutes) || minutes <= 0) return "-";
  const hours = Math.trunc(minutes / 60);
  const mins = Math.trunc(minutes % 60);
  if (hours === 0) return `${mins} min`;
  if (mins === 0) return `${hours} hr`;
  return `${hours} hr ${mins} min`;
};

const statusChipSx = (theme: Theme, status?: number) => {
  const c = theme.palette.customColors;
  const { orange, blushPink } = theme.palette;
  const tones: Record<number, { color: string; bg: string; border: string }> = {
    [StockCheckRequestStatus.OPEN]: {
      color: c.darkBlue,
      bg: c.blueLightBackground,
      border: c.softBorder,
    },
    [StockCheckRequestStatus.CLOSED]: {
      color: c.successGreen,
      bg: c.successGreenLightBackground,
      border: c.successGreenLightBorder,
    },
    [StockCheckRequestStatus.REJECTED]: {
      color: c.errorRed,
      bg: blushPink.main,
      border: c.borderErrorRedColor,
    },
    [StockCheckRequestStatus.EXPIRED]: {
      color: orange.dark,
      bg: c.lightOrange,
      border: orange.light,
    },
  };
  const tone = (status && tones[status]) || {
    color: c.grayText,
    bg: c.veryLightGray,
    border: c.lightGray,
  };
  return {
    height: 20,
    fontSize: "0.68rem",
    fontWeight: 700,
    borderRadius: "999px",
    color: tone.color,
    backgroundColor: tone.bg,
    border: `1px solid ${tone.border}`,
    "& .MuiChip-label": { px: 1.1 },
  };
};

const typeChipSx = (theme: Theme) => {
  const c = theme.palette.customColors;
  return {
    height: 20,
    fontSize: "0.68rem",
    fontWeight: 600,
    borderRadius: "999px",
    color: c.grayText,
    backgroundColor: c.white,
    border: `1px solid ${c.switchTrackGray}`,
    "& .MuiChip-label": { px: 1.1 },
  };
};

const InfoColumn: React.FC<{
  label: string;
  primary: React.ReactNode;
  secondary?: React.ReactNode;
  showDivider?: boolean;
}> = ({ label, primary, secondary, showDivider = true }) => {
  const theme = useTheme<Theme>();
  const colors = getModernPageColors(theme);
  const divider = getCardDivider(theme);

  return (
    <Box
      sx={{
        minWidth: 0,
        px: { xs: 0, md: 1.5 },
        py: { xs: 0.5, md: 0 },
        position: "relative",
        borderBottom: { xs: divider, md: "none" },
        "&::after": {
          content: '""',
          display: { xs: "none", md: showDivider ? "block" : "none" },
          position: "absolute",
          right: 0,
          top: 2,
          bottom: 2,
          width: "1px",
          backgroundColor: theme.palette.customColors.lightGray,
        },
      }}
    >
      <CustomTypography
        variant="caption"
        color={colors.subtitle}
        fontWeight={500}
        sx={{ display: "block", mb: 0.1, fontSize: "0.68rem", lineHeight: 1.2 }}
      >
        {label}
      </CustomTypography>
      <CustomTypography
        variant="body2"
        fontWeight={700}
        color={colors.title}
        title={typeof primary === "string" ? primary : undefined}
        sx={{
          display: "block",
          fontSize: "0.82rem",
          lineHeight: 1.2,
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
        }}
      >
        {primary}
      </CustomTypography>
      {secondary ? (
        <CustomTypography
          variant="caption"
          color={colors.subtitle}
          fontWeight={500}
          title={typeof secondary === "string" ? secondary : undefined}
          sx={{
            display: "block",
            mt: 0.2,
            fontSize: "0.75rem",
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {secondary}
        </CustomTypography>
      ) : null}
    </Box>
  );
};

const StockCheckRequestCard = ({ stock }: StockCheckRequestCardProps) => {
  const theme = useTheme<Theme>();
  const colors = theme.palette.customColors;
  const pageColors = getModernPageColors(theme);
  const divider = getCardDivider(theme);
  const note = stock.notes?.trim() || "";
  const statusLabel =
    titleCase(stock.status_label) ||
    getStockCheckRequestStatusLabel(stock.status) ||
    "Pending";
  const orderType = titleCase(stock.order_type);
  const orderPath = stock.order_id
    ? `/orders/add-orders?orderId=${stock.order_id}`
    : "";

  const openOrder = () => {
    if (!orderPath) return;
    window.open(orderPath, "_blank");
  };

  const viewButtonSx = {
    minWidth: "fit-content",
    minHeight: 28,
    height: 28,
    px: 1.75,
    fontSize: "0.75rem",
    fontWeight: 700,
    borderRadius: "8px",
    boxShadow: "none",
    color: colors.purpleMain,
    backgroundColor: colors.lightPurple,
    "&:hover": {
      backgroundColor: theme.palette.lavenderBlue.main,
      boxShadow: "none",
    },
  };

  return (
    <Box
      sx={{
        borderRadius: "12px",
        border: divider,
        backgroundColor: getCardBackgroundColor(stock, theme),
        overflow: "hidden",
      }}
    >
      <Box
        sx={{
          px: 1.5,
          py: 0.5,
          display: "flex",
          alignItems: "center",
          gap: 0.75,
          flexWrap: "wrap",
          borderBottom: divider,
        }}
      >
        <CustomTypography
          variant="heading7"
          fontWeight={800}
          color={pageColors.title}
          sx={{ fontSize: "0.9rem", lineHeight: 1.2 }}
        >
          #{stock.id ?? "-"}
        </CustomTypography>
        <Chip label={statusLabel} size="small" sx={statusChipSx(theme, stock.status)} />
        {orderType ? (
          <Chip label={orderType} size="small" sx={typeChipSx(theme)} />
        ) : null}
      </Box>

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: {
            xs: "1fr",
            sm: "1fr 1fr",
            md: "1.3fr 1.1fr 0.9fr 1fr 1.1fr",
          },
          px: { xs: 1.5, md: 0.75 },
          py: { xs: 0.25, md: 0.5 },
          alignItems: "stretch",
        }}
      >
        <InfoColumn
          label="Order"
          primary={
            orderPath ? (
              <Box
                component={Link}
                to={orderPath}
                onClick={(event) => event.stopPropagation()}
                sx={{
                  color: colors.darkBlue,
                  textDecoration: "underline",
                  fontWeight: 700,
                }}
              >
                {`Order #${stock.order_id}`}
              </Box>
            ) : (
              "-"
            )
          }
          secondary={stock.order_label?.trim() || undefined}
        />
        <InfoColumn label="Requestor" primary={stock.requestor_name?.trim() || "-"} />
        <InfoColumn label="TAT" primary={formatTat(stock.tat)} />
        <InfoColumn label="Closer" primary={stock.closer_name?.trim() || "-"} />
        <InfoColumn
          label="Modified"
          primary={stock.modified_date ? formatDate(stock.modified_date) : "-"}
          showDivider={false}
        />
      </Box>

      <Box
        sx={{
          px: 1.5,
          py: 0.5,
          borderTop: divider,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 1,
          minWidth: 0,
          flexWrap: { xs: "wrap", sm: "nowrap" },
        }}
      >
        <Stack
          direction="row"
          spacing={0.5}
          useFlexGap
          flexWrap="wrap"
          alignItems="center"
          sx={{ minWidth: 0 }}
        >
          {note ? (
            <Box
              sx={{
                px: 1,
                py: 0.25,
                borderRadius: "999px",
                backgroundColor: colors.lightPurple,
                width: "fit-content",
                maxWidth: { xs: "min(224px, 100%)", sm: 280, md: 360 },
                minWidth: 0,
                overflow: "hidden",
              }}
            >
              <CustomTypography
                variant="caption"
                color={pageColors.title}
                fontWeight={600}
                title={note}
                sx={{
                  fontSize: "0.68rem",
                  display: "block",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {`Note - ${note}`}
              </CustomTypography>
            </Box>
          ) : null}
          <CustomTypography
            variant="caption"
            color={pageColors.subtitle}
            fontWeight={600}
            sx={{ fontSize: "0.68rem", lineHeight: 1.2 }}
          >
            {`Requested by ${stock.requestor_name?.trim() || "-"} | Updated on ${
              stock.modified_date ? formatDate(stock.modified_date) : "-"
            }${
              stock.closer_name?.trim()
                ? ` | Closed by ${stock.closer_name.trim()}`
                : ""
            }`}
          </CustomTypography>
        </Stack>
        <AppButton
          label="View"
          onClick={openOrder}
          disabled={!orderPath}
          customStyles={viewButtonSx}
        />
      </Box>
    </Box>
  );
};

export default StockCheckRequestCard;
